import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Eye,
  EyeOff,
  Shield,
  Lock,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { InlineLoadSkeleton } from "../components/common/InlineLoadSkeleton";

const ChangePasswordPage = () => {
  const { changePassword, user } = useAuth();
  const navigate = useNavigate();
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Check if this is first-time password change
  useEffect(() => {
    if (user && user.is_password_changed === false) {
      setIsFirstTime(true);
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
    setSuccess("");
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChar,
      isValid:
        minLength &&
        hasUpperCase &&
        hasLowerCase &&
        hasNumbers &&
        hasSpecialChar,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validation
    if (formData.newPassword !== formData.confirmPassword) {
      setError("New password and confirm password do not match");
      setLoading(false);
      return;
    }

    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      setError(
        "Password must be at least 8 characters long and contain uppercase, lowercase, numbers, and special characters",
      );
      setLoading(false);
      return;
    }

    try {
      await changePassword(
        formData.currentPassword,
        formData.newPassword,
        isFirstTime,
      );
      setSuccess("Password changed successfully! Redirecting...");

      // Redirect after 2 seconds based on user role (UI-based, not from backend)
      setTimeout(() => {
        const userRole = user?.role;
        // Chief admin goes to /clients
        if (userRole === "chief-admin" || userRole === "Chief Admin") {
          navigate("/clients", { replace: true });
        } else {
          // All other users go to /projects
          navigate("/projects", { replace: true });
        }
      }, 2000);
    } catch (err) {
      let errorMessage = "Failed to change password. Please try again.";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 400) {
        errorMessage =
          "Current password is incorrect or new password is invalid.";
      } else if (err.response?.status === 401) {
        errorMessage = "Session expired. Please login again.";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const passwordValidation = validatePassword(formData.newPassword);

  return (
    <div className="min-h-screen bg-bg-secondary flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-bg-primary rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-text-primary mb-2">
              {isFirstTime ? "Set Your Password" : "Change Password"}
            </h1>
            <p className="text-text-secondary">
              {isFirstTime
                ? "Please set a secure password for your account"
                : "Please set a secure password for your account"}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <CheckCircle2 className="w-5 h-5 text-green-600 mr-2" />
                <p className="text-green-800 text-sm">{success}</p>
              </div>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="space-y-6"
            autoComplete="off"
          >
            {/* Current Password - Only show if not first time */}
            {!isFirstTime && (
              <div>
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative mt-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-text-tertiary" />
                  </div>
                  <Input
                    type={showPasswords.current ? "text" : "password"}
                    id="currentPassword"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    className="pl-10 pr-10"
                    placeholder="Enter current password"
                    autoComplete="off"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => togglePasswordVisibility("current")}
                  >
                    {showPasswords.current ? (
                      <EyeOff className="h-5 w-5 text-text-tertiary" />
                    ) : (
                      <Eye className="h-5 w-5 text-text-tertiary" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* New Password */}
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-text-tertiary" />
                </div>
                <Input
                  type={showPasswords.new ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className="pl-10 pr-10"
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => togglePasswordVisibility("new")}
                >
                  {showPasswords.new ? (
                    <EyeOff className="h-5 w-5 text-text-tertiary" />
                  ) : (
                    <Eye className="h-5 w-5 text-text-tertiary" />
                  )}
                </button>
              </div>

              {/* Password Requirements */}
              {formData.newPassword && (
                <div className="mt-2 space-y-1">
                  <div
                    className={`text-xs flex items-center ${passwordValidation.minLength ? "text-green-600" : "text-text-tertiary"}`}
                  >
                    <span className="mr-1">
                      {passwordValidation.minLength ? "✓" : "○"}
                    </span>
                    At least 8 characters
                  </div>
                  <div
                    className={`text-xs flex items-center ${passwordValidation.hasUpperCase ? "text-green-600" : "text-text-tertiary"}`}
                  >
                    <span className="mr-1">
                      {passwordValidation.hasUpperCase ? "✓" : "○"}
                    </span>
                    One uppercase letter
                  </div>
                  <div
                    className={`text-xs flex items-center ${passwordValidation.hasLowerCase ? "text-green-600" : "text-text-tertiary"}`}
                  >
                    <span className="mr-1">
                      {passwordValidation.hasLowerCase ? "✓" : "○"}
                    </span>
                    One lowercase letter
                  </div>
                  <div
                    className={`text-xs flex items-center ${passwordValidation.hasNumbers ? "text-green-600" : "text-text-tertiary"}`}
                  >
                    <span className="mr-1">
                      {passwordValidation.hasNumbers ? "✓" : "○"}
                    </span>
                    One number
                  </div>
                  <div
                    className={`text-xs flex items-center ${passwordValidation.hasSpecialChar ? "text-green-600" : "text-text-tertiary"}`}
                  >
                    <span className="mr-1">
                      {passwordValidation.hasSpecialChar ? "✓" : "○"}
                    </span>
                    One special character
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-text-tertiary" />
                </div>
                <Input
                  type={showPasswords.confirm ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="pl-10 pr-10"
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => togglePasswordVisibility("confirm")}
                >
                  {showPasswords.confirm ? (
                    <EyeOff className="h-5 w-5 text-text-tertiary" />
                  ) : (
                    <Eye className="h-5 w-5 text-text-tertiary" />
                  )}
                </button>
              </div>
              {formData.confirmPassword &&
                formData.newPassword !== formData.confirmPassword && (
                  <p className="mt-1 text-xs text-red-600">
                    Passwords do not match
                  </p>
                )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={
                loading ||
                !passwordValidation.isValid ||
                formData.newPassword !== formData.confirmPassword ||
                (!isFirstTime && !formData.currentPassword)
              }
              className="w-full"
            >
              {loading ? (
                <>
                  <InlineLoadSkeleton className="h-4 w-4 mr-2" />
                  Changing...
                </>
              ) : (
                <>
                  Change Password
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
